import React, { useMemo, useState } from 'react';
import { opsApi } from '../../../api/dbOps';
import { errorText } from '../../../api/migrationRunner';
import { Alert, Btn, Dialog, ErrorBox, Panel, Pill, Spinner, bytes, when } from '../runner/ui';
import { useApprovalGate } from '../runner/ApprovalGate';
import { input, sel, td, th, usePoll } from './shared';

/**
 * Company data (approved mockup "Database redesign", Company data): export
 * everything (CSV zip, emailed 24 h link) or delete a company's data (final
 * backup kept 90 days, needs a second owner and a fresh sign-in).
 */
const EXPORT_TONE = { queued: 'mut', running: 'info', ready: 'ok', failed: 'bad', expired: 'mut' } as const;
const DELETE_TONE = { running: 'info', done: 'ok', failed: 'bad' } as const;

const CompanyDataTab: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const servers = usePoll(() => opsApi.servers(), null);
  const companies = useMemo(
    () => (servers.data || []).flatMap((s) => s.companies).sort((a, b) => a.name.localeCompare(b.name)),
    [servers.data]
  );
  const [companyId, setCompanyId] = useState('');
  const company = companies.find((c) => c.id === companyId) || null;
  const exportsQ = usePoll(() => opsApi.exports(), 10000);
  const deletions = usePoll(() => opsApi.deletions(), 10000);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(false);
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [delErr, setDelErr] = useState<string | null>(null);
  const gate = useApprovalGate();

  const startExport = async () => {
    if (!company) return;
    setBusy(true);
    try {
      await opsApi.startExport(company.id);
      setMsg({ tone: 'ok', text: `Exporting ${company.name}. You'll get an email with the download link (valid 24 hours).` });
      void exportsQ.reload();
    } catch (err) {
      setMsg({ tone: 'bad', text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const startDelete = async () => {
    if (!company) return;
    setBusy(true);
    setDelErr(null);
    try {
      const went = await gate.guard((approvalId) => opsApi.deleteCompanyData({ companyId: company.id, confirmCode: code.trim(), reason: reason.trim() }, approvalId));
      if (went) {
        setDel(false);
        setCode('');
        setReason('');
        setMsg({ tone: 'ok', text: `Deleting ${company.name}'s data: final backup first, then the database and files.` });
        void deletions.reload();
      }
    } catch (err) {
      setDelErr(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const schemaMissing = (exportsQ.data && !exportsQ.data.schema_ready) || (deletions.data && !deletions.data.schema_ready);

  return (
    <div className="space-y-4">
      {schemaMissing && <Alert tone="warn" title="Needs the registry migration migrations-2026-10-11-db-ops">Exports and deletions are recorded there.</Alert>}
      <div className="flex flex-wrap items-center gap-2">
        <select className={sel} value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-label="Company">
          <option value="">Pick a company</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.database})</option>)}
        </select>
        {servers.loading && <Spinner />}
      </div>
      {msg && <Alert tone={msg.tone} title={msg.text} action={<Btn kind="ghost" small onClick={() => setMsg(null)}>Dismiss</Btn>} />}

      {company && (
        <Panel title={`${company.name} · data`}>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-4">
              <b className="block text-slate-900">Export everything</b>
              <p className="mt-1 text-[13px] text-slate-600">All tables as CSV in one zip, plus a list of every photo and document and where it is stored. Link emailed to you, valid 24 hours.</p>
              {isOwner && <Btn className="mt-3" disabled={busy} onClick={startExport}>Export</Btn>}
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50/40 p-4">
              <b className="block text-slate-900">Delete the company's data</b>
              <p className="mt-1 text-[13px] text-slate-600">Takes a final backup (kept 90 days), removes the database and files, and leaves a record of who, when and why. Needs a second owner.</p>
              {isOwner && <Btn kind="danger" className="mt-3" disabled={busy} onClick={() => { setDelErr(null); setDel(true); }}>Delete data…</Btn>}
            </div>
          </div>
        </Panel>
      )}

      <Panel title="Exports" bodyClass="p-0">
        {(exportsQ.data?.exports || []).length === 0 ? <div className="p-4 text-[13px] text-slate-500">No exports yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50"><tr><th className={th}>When</th><th className={th}>Who</th><th className={th}>Company</th><th className={th}>Size</th><th className={th}>Status</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {exportsQ.data!.exports.map((e) => (
                  <tr key={e.id}>
                    <td className={`${td} whitespace-nowrap`}>{when(e.created_at)}</td>
                    <td className={td}>{e.requested_by}</td>
                    <td className={td}>{e.company_name}</td>
                    <td className={`${td} tabular-nums`}>{e.size_bytes ? `${bytes(e.size_bytes)} · ${e.tables_count} tables` : '—'}</td>
                    <td className={td}>
                      <Pill tone={EXPORT_TONE[e.status]}>{e.status === 'ready' ? `link emailed, until ${when(e.expires_at)}` : e.status}</Pill>
                      {e.error_message && <div className="mt-1 text-[11.5px] text-red-700">{e.error_message}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Deleted companies" bodyClass="p-0">
        {(deletions.data?.deletions || []).length === 0 ? <div className="p-4 text-[13px] text-slate-500">None.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50"><tr><th className={th}>When</th><th className={th}>Company</th><th className={th}>Who / why</th><th className={th}>Final backup</th><th className={th}>Files</th><th className={th}>Status</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {deletions.data!.deletions.map((d) => (
                  <tr key={d.id}>
                    <td className={`${td} whitespace-nowrap`}>{when(d.created_at)}</td>
                    <td className={td}>{d.company_name}<div className="text-[11.5px] text-slate-500">{d.database_name}</div></td>
                    <td className={td}>{d.requested_by}<div className="text-[11.5px] text-slate-500">{d.reason}</div></td>
                    <td className={td}>{d.backup_id ? (d.backup_purged_at ? 'removed' : `${d.backup_storage}, kept until ${when(d.backup_keep_until)}`) : '—'}</td>
                    <td className={td}>{d.files_note || '—'}</td>
                    <td className={td}>
                      <Pill tone={DELETE_TONE[d.status]}>{d.status}</Pill>
                      {d.error_message && <div className="mt-1 max-w-xs text-[11.5px] text-red-700">{d.error_message}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Dialog
        open={del && !!company}
        onClose={() => setDel(false)}
        title={`Delete all of ${company?.name ?? ''}'s data?`}
        footer={(
          <>
            <Btn kind="ghost" onClick={() => setDel(false)}>Cancel</Btn>
            <Btn kind="danger" disabled={busy || !reason.trim() || code.trim().toLowerCase() !== (company?.code || '').toLowerCase()} onClick={startDelete}>
              {busy && <Spinner />}Delete the company's data
            </Btn>
          </>
        )}
      >
        <p>This takes a verified final backup (kept 90 days), then drops <b>{company?.database}</b>, removes the company from the registry and removes its stored files where that's safe. It can't be undone except by restoring the backup.</p>
        <label className="block text-[13px] font-semibold text-slate-700">
          Why
          <input className={`${input} mt-1`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Account closed, customer asked for deletion" />
        </label>
        <label className="block text-[13px] font-semibold text-slate-700">
          Type the company code <code>{company?.code}</code> to confirm
          <input className={`${input} mt-1`} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
        </label>
        <p className="text-[12.5px] text-slate-500">Needs a fresh sign-in and another owner's approval.</p>
        {delErr && <ErrorBox>{delErr}</ErrorBox>}
      </Dialog>
      {gate.element}
    </div>
  );
};

export default CompanyDataTab;
